import type {PayloadAction} from "@reduxjs/toolkit";
import {createSlice} from "@reduxjs/toolkit";

import type {LocationInfoDescriptionData} from "@reva/shared/description-keywords";
import type {PoiVehicle} from "@reva/shared/poi";
import type {LocationInfoTimelinesData} from "@reva/shared/timelines";

import type {AvmApiLocationInfoClimateRisk} from "../api/payload-types/locationinfo-climate-risk";
import type {AvmApiLocationInfoEmission} from "../api/payload-types/locationinfo-emission";
import type {AvmApiLocationInfoNoise} from "../api/payload-types/locationinfo-noise";
import type {AvmApiLocationInfoPois} from "../api/payload-types/locationinfo-pois";
import type {AvmApiLocationInfoPriceIndex} from "../api/payload-types/locationinfo-priceindex";
import type {AvmApiLocationInfoStandardGroundValue} from "../api/payload-types/locationinfo-standard-ground-value";
import {buildApiQueryError} from "../util/errorUtil";
import type {ApiEndpointState, StateKeysWhichAreApiEndpointState, StateValueForeApiEndpointKey} from "./types";
import {createEmptyState} from "./types";

interface LocationInfoState {
    climateRisk: ApiEndpointState<AvmApiLocationInfoClimateRisk>;
    description: ApiEndpointState<LocationInfoDescriptionData>;
    emission: ApiEndpointState<AvmApiLocationInfoEmission>;
    noise: ApiEndpointState<AvmApiLocationInfoNoise>;
    pois: ApiEndpointState<AvmApiLocationInfoPois>;
    priceIndex: ApiEndpointState<AvmApiLocationInfoPriceIndex>;
    standardGroundValue: ApiEndpointState<AvmApiLocationInfoStandardGroundValue>;
    timelines: ApiEndpointState<LocationInfoTimelinesData>;

    showTrafficMap: boolean;
    selectedVehicle: PoiVehicle | "all";
}

/**
 * Type describing all "endpoints" (keys) of the state, which are defined as `ApiEndpointState`.
 */
export type LocationInfoApiEndpointKey = StateKeysWhichAreApiEndpointState<LocationInfoState>;

/**
 * Type resulting in the typed `data` property of the `ApiEndpointState` corresponding to a `LocationInfoApiEndpointKey`
 */
export type LocationInfoApiEndpointType<K extends LocationInfoApiEndpointKey> = StateValueForeApiEndpointKey<
    LocationInfoState,
    K
>;

/**
 * Initial state for LocationInfo.
 */
const initialState: LocationInfoState = {
    climateRisk: createEmptyState(),
    description: createEmptyState(),
    emission: createEmptyState(),
    noise: createEmptyState(),
    pois: createEmptyState(),
    priceIndex: createEmptyState(),
    standardGroundValue: createEmptyState(),
    timelines: createEmptyState(),

    showTrafficMap: true,
    selectedVehicle: "all",
};

// --- Begin: Custom (generic) Action "setEndpointData" ---

type SetDataPayload<K extends LocationInfoApiEndpointKey> = {
    endpoint: K;
    data: LocationInfoApiEndpointType<K>;
};

const setEndpointDataName = "setEndpointData";

export function setEndpointData<K extends LocationInfoApiEndpointKey>(
    endpoint: K,
    data: LocationInfoApiEndpointType<K>,
): PayloadAction<SetDataPayload<K>> {
    return {
        type: setEndpointDataName,
        payload: {endpoint, data},
    };
}

setEndpointData.type = setEndpointDataName;

// --- End: Custom (generic) Action "setEndpointData" ---

const locationInfoSlice = createSlice({
    name: "locationInfo",
    initialState: initialState,
    reducers: {
        clearAllLocationInfoData: (state) => {
            for (const [prop, initialValue] of Object.entries(initialState)) {
                const propKey = prop as keyof typeof state;
                if (typeof initialValue === "object" && typeof initialValue.loading === "boolean") {
                    (state[propKey] as object) = createEmptyState();
                }
            }
            state.showTrafficMap = initialState.showTrafficMap;
            state.selectedVehicle = initialState.selectedVehicle;
        },
        setEndpointLoading: (state, action: PayloadAction<{endpoint: LocationInfoApiEndpointKey}>) => {
            const {endpoint} = action.payload;
            if (endpoint && state[endpoint]) {
                state[endpoint].loading = true;
                state[endpoint].queryError = null;
                state[endpoint].data = null;
            }
        },
        setEndpointQueryError: (
            state,
            // TODO: Migrate QueryError / ErrorUtils to TypeScript
            action: PayloadAction<{endpoint: LocationInfoApiEndpointKey; queryError: any}>,
        ) => {
            const {endpoint, queryError} = action.payload;
            if (endpoint && state[endpoint]) {
                state[endpoint].loading = false;
                state[endpoint].queryError = buildApiQueryError(queryError);
                state[endpoint].data = null;
            }
        },
        setShowTrafficMap: (state, action: PayloadAction<boolean>) => {
            state.showTrafficMap = action.payload;
        },
        setSelectedVehicle: (state, action: PayloadAction<PoiVehicle | "all">) => {
            state.selectedVehicle = action.payload;
        },
    },
    extraReducers: (builder) => {
        // THis is required for (generic) action creator "setEndpointData"
        builder.addCase(setEndpointData, (state, action) => {
            state[action.payload.endpoint].data = action.payload.data;
            const {endpoint, data} = action.payload;
            if (endpoint && state[endpoint]) {
                state[endpoint].loading = false;
                state[endpoint].queryError = null;
                state[endpoint].data = data;
            }
        });
    },
    selectors: {
        // Generic interface
        selectEndpointLoading: (state, endpoint: LocationInfoApiEndpointKey) => state?.[endpoint]?.loading ?? false,
        selectEndpointError: (state, endpoint: LocationInfoApiEndpointKey) => state?.[endpoint]?.queryError || null,
        selectEndpointData: (state, endpoint: LocationInfoApiEndpointKey) => state?.[endpoint]?.data || null,

        // individual selectors (for type safety)
        selectClimateRiskData: (state) => state.climateRisk.data,
        selectDescriptionData: (state) => state.description.data,
        selectEmissionData: (state) => state.emission.data,
        selectNoiseData: (state) => state.noise.data,
        selectPoisData: (state) => state.pois.data,
        selectPriceIndexData: (state) => state.priceIndex.data,
        selectStandardGroundValueData: (state) => state.standardGroundValue.data,
        selectTimelinesData: (state) => state.timelines.data,

        selectClimateRiskLoading: (state) => state.climateRisk.loading,
        selectDescriptionLoading: (state) => state.description.loading,
        selectEmissionLoading: (state) => state.emission.loading,
        selectNoiseLoading: (state) => state.noise.loading,
        selectPoisLoading: (state) => state.pois.loading,
        selectPriceIndexLoading: (state) => state.priceIndex.loading,
        selectStandardGroundValueLoading: (state) => state.standardGroundValue.loading,
        selectTimelinesLoading: (state) => state.timelines.loading,

        selectClimateRiskError: (state) => state.climateRisk.queryError,
        selectDescriptionError: (state) => state.description.queryError,
        selectEmissionError: (state) => state.emission.queryError,
        selectNoiseError: (state) => state.noise.queryError,
        selectPoisError: (state) => state.pois.queryError,
        selectPriceIndexError: (state) => state.priceIndex.queryError,
        selectStandardGroundValueError: (state) => state.standardGroundValue.queryError,
        selectTimelinesError: (state) => state.timelines.queryError,

        selectShowTrafficMap: (state) => state.showTrafficMap,
        selectSelectedVehicle: (state) => state.selectedVehicle,
    },
});

export const {
    clearAllLocationInfoData,
    setEndpointLoading,
    setEndpointQueryError,
    // setEndpointData, // Hint: setEndpointData is implemented as a custom (generic) action creator function
    setShowTrafficMap,
    setSelectedVehicle,
} = locationInfoSlice.actions;

export const {
    selectEndpointLoading,
    selectEndpointError,
    selectEndpointData,

    selectClimateRiskData,
    selectDescriptionData,
    selectEmissionData,
    selectNoiseData,
    selectPoisData,
    selectPriceIndexData,
    selectStandardGroundValueData,
    selectTimelinesData,

    selectClimateRiskLoading,
    selectDescriptionLoading,
    selectEmissionLoading,
    selectNoiseLoading,
    selectPoisLoading,
    selectPriceIndexLoading,
    selectStandardGroundValueLoading,
    selectTimelinesLoading,

    selectClimateRiskError,
    selectDescriptionError,
    selectEmissionError,
    selectNoiseError,
    selectPoisError,
    selectPriceIndexError,
    selectStandardGroundValueError,
    selectTimelinesError,

    selectShowTrafficMap,
    selectSelectedVehicle,
} = locationInfoSlice.selectors;

export default locationInfoSlice.reducer;
